import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useParams } from '@tanstack/react-router';

import { Facility } from '@crossnokaye/typescript-sdk/models/components';

import { useUserState } from '@/hooks/use-user-state';
import CurrentFacilityAtoms from '@/state/current-facility.state';
import RouteAtoms from '@/state/route.state';
import { baseTimeZoneAtom } from '@/state/time.state';
import UserStateAtoms from '@/state/user.state';

type CurrentFacility = {
  agentId?: string;
  currentFacility?: Facility;
  facilityId?: string;
  facilitySlug?: string;
  facilityUrlName?: string;
  organizationId?: string;
  orgName?: string;
  orgUrlName?: string;
};

export const useCurrentFacility = (): CurrentFacility => {
  const setBaseTimeZone = useSetAtom(baseTimeZoneAtom);
  const setUserState = useSetAtom(UserStateAtoms.user);
  const setRouteParams = useSetAtom(RouteAtoms.route);
  const looseParams = useParams({ strict: false });

  const { data: userState } = useUserState();

  // Sync userState to Jotai atom
  useEffect(() => {
    setUserState(userState);
  }, [userState, setUserState]);

  // Sync route params to Jotai atom
  useEffect(() => {
    setRouteParams({
      org: looseParams['org'],
      agent: looseParams['agent'],
    });
  }, [looseParams, setRouteParams]);

  // Read computed values from Jotai atoms
  const currentFacility = useAtomValue(CurrentFacilityAtoms.facility);
  const currentOrganization = useAtomValue(CurrentFacilityAtoms.organization);
  const agentId = useAtomValue(CurrentFacilityAtoms.agentId);

  // Side effect for timezone
  useEffect(() => {
    setBaseTimeZone(currentFacility?.timezone);
  }, [currentFacility?.timezone, setBaseTimeZone]);

  return {
    agentId,
    currentFacility,
    facilityId: currentFacility?.facilityId,
    facilitySlug: currentFacility?.shortName,
    facilityUrlName: currentFacility?.shortName,
    organizationId: currentOrganization?.organizationId,
    orgName: currentOrganization?.displayName,
    orgUrlName: currentOrganization?.name,
  };
};
